import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Users, GraduationCap, School, IndianRupee, CheckCircle, XCircle,
  Clock, TrendingUp, Calendar
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard');
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const attendance = dashboardData?.attendance || {};

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Welcome back, {user?.first_name}!
        </h1>
        <p className="text-blue-100">
          Here's what's happening in your school today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Students"
          value={stats.total_students || 0}
          icon={GraduationCap}
          color="blue"
        />
        <StatCard
          title="Total Teachers"
          value={stats.total_teachers || 0}
          icon={Users}
          color="green"
        />
        <StatCard
          title="Total Classes"
          value={stats.total_classes || stats.total_schools || 0}
          icon={School}
          color="purple"
        />
        <StatCard
          title="Fee Collection"
          value={`₹${(stats.fee_collection_this_month || 0).toLocaleString()}`}
          icon={IndianRupee}
          color="amber"
        />
      </div>

      {/* Attendance & Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Attendance */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Today's Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{attendance.present || 0}</p>
                <p className="text-sm text-slate-400">Present</p>
              </div>
              <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{attendance.absent || 0}</p>
                <p className="text-sm text-slate-400">Absent</p>
              </div>
              <div className="text-center p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{attendance.late || 0}</p>
                <p className="text-sm text-slate-400">Late</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fee Collection Chart */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Fee Collection Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.fee_chart || []).slice(-4).map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-sm text-slate-400 w-20">{item.month}</span>
                  <div className="flex-1 bg-slate-700 rounded-full h-4">
                    <div
                      className="bg-blue-500 h-4 rounded-full transition-all"
                      style={{ width: `${Math.min((item.amount / 100000) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-white w-24 text-right">
                    ₹{item.amount?.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Announcements */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Recent Announcements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.announcements || []).length > 0 ? (
                dashboardData.announcements.map((ann) => (
                  <div key={ann.id} className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                    <h4 className="font-medium text-white">{ann.title}</h4>
                    <p className="text-sm text-slate-400 mt-1 line-clamp-2">{ann.content}</p>
                  </div>
                ))
              ) : (
                <p className="text-slate-400 text-center py-4">No announcements</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardData?.upcoming_events || []).length > 0 ? (
                dashboardData.upcoming_events.map((event) => (
                  <div key={event.id} className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex flex-col items-center justify-center">
                      <span className="text-xs text-blue-400">
                        {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                      <span className="text-lg font-bold text-white">
                        {new Date(event.start_date).getDate()}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{event.title}</h4>
                      <p className="text-sm text-slate-400">{event.event_type}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-400 text-center py-4">No upcoming events</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-500',
    green: 'bg-green-500/10 border-green-500/30 text-green-500',
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-500',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-500',
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400 mb-1">{title}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Dashboard;
